import React, { useState, useEffect } from 'react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';

const SupplierTypes = () => {
  // State for supplier types list
  const [types, setTypes] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);
  
  // State for form data
  const [formData, setFormData] = useState({
    suppliertypeid: null,
    typecode: '',
    typename: '',
    description: '',
    displayorder: 0,
    color: '',
    icon: ''
  });

  // State for UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showWithCount, setShowWithCount] = useState(false);

  // Predefined icons for selection
  const iconOptions = [
    { value: '🏢', label: '🏢 Building' },
    { value: '👷', label: '👷 Contractor' },
    { value: '⚙️', label: '⚙️ Service' },
    { value: '📦', label: '📦 Package' },
    { value: '🚛', label: '🚛 Truck' },
    { value: '✉️', label: '✉️ Mail' },
    { value: '🏭', label: '🏭 Factory' },
    { value: '🛃', label: '🛃 Customs' },
    { value: '🛡️', label: '🛡️ Shield' },
    { value: '📋', label: '📋 List' },
    { value: '🔧', label: '🔧 Tool' },
    { value: '💼', label: '💼 Briefcase' },
    { value: '🌐', label: '🌐 Globe' },
    { value: '📞', label: '📞 Phone' },
    { value: '🎯', label: '🎯 Target' }
  ];

  // Load data on component mount
  useEffect(() => {
    loadTypes();
  }, [showWithCount]);

  // Filter types when search term changes
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredTypes(types);
    } else {
      const filtered = types.filter(type =>
        type.typecode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        type.typename.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (type.description && type.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredTypes(filtered);
    }
  }, [searchTerm, types]);

  // Load supplier types
  const loadTypes = async () => {
    try {
      setLoading(true);
      const endpoint = showWithCount ? '/SupplierTypes.php?with_count' : '/SupplierTypes.php';
      const response = await apiClient.get(endpoint);
      if (response.data.success) {
        setTypes(response.data.data.types || []);
        setFilteredTypes(response.data.data.types || []);
      }
    } catch (err) {
      console.error('Error loading supplier types:', err);
      setError('Failed to load supplier types');
    } finally {
      setLoading(false);
    }
  };

  // Generate type code
  const generateTypeCode = async () => {
    try {
      const response = await apiClient.get('/SupplierTypes.php?generate_code');
      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          typecode: response.data.data.typecode
        }));
      }
    } catch (err) {
      console.error('Error generating type code:', err);
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle number input change
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? 0 : parseInt(value)
    }));
  };

  // Validate form
  const validateForm = () => {
    if (!formData.typecode.trim()) {
      setError('Type code is required');
      return false;
    }
    if (!formData.typename.trim()) {
      setError('Type name is required');
      return false;
    }
    return true;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      let response;
      if (editingId) {
        // Update existing type
        response = await apiClient.put('/SupplierTypes.php', formData);
      } else {
        // Create new type
        response = await apiClient.post('/SupplierTypes.php', formData);
      }

      if (response.data.success) {
        await loadTypes();
        handleClear();
        setShowForm(false);
      } else {
        setError(response.data.message || 'Failed to save supplier type');
      }
    } catch (err) {
      console.error('Error saving supplier type:', err);
      setError('Failed to save supplier type: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (type) => {
    setFormData({
      suppliertypeid: type.suppliertypeid,
      typecode: type.typecode || '',
      typename: type.typename || '',
      description: type.description || '',
      displayorder: parseInt(type.displayorder) || 0,
      color: type.color || '',
      icon: type.icon || ''
    });
    setEditingId(type.suppliertypeid);
    setShowForm(true);
    setError('');
  };

  // Handle delete
  const handleDelete = async (typeId) => {
    if (!window.confirm('Are you sure you want to delete this supplier type?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.delete('/SupplierTypes.php', {
        data: { suppliertypeid: typeId }
      });

      if (response.data.success) {
        await loadTypes();
      } else {
        setError(response.data.message || 'Failed to delete supplier type');
      }
    } catch (err) {
      console.error('Error deleting supplier type:', err);
      setError('Failed to delete supplier type: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle clear form
  const handleClear = () => {
    setFormData({
      suppliertypeid: null,
      typecode: '',
      typename: '',
      description: '',
      displayorder: 0,
      color: '',
      icon: ''
    });
    setEditingId(null);
    setError('');
  };

  // Handle new
  const handleNew = async () => {
    handleClear();
    await generateTypeCode();
    setShowForm(true);
  };

  // Handle cancel
  const handleCancel = () => {
    handleClear();
    setShowForm(false);
  };

  return (
    <div className="master-form-container">
      <div className="master-form-header">
        <h2>📋 Supplier Type Management</h2>
        <div className="header-actions">
          {!showForm && (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', marginRight: '10px' }}>
                <input
                  type="checkbox"
                  checked={showWithCount}
                  onChange={(e) => setShowWithCount(e.target.checked)}
                />
                <span>Show Supplier Count</span>
              </label>
              <button className="btn btn-primary" onClick={handleNew}>
                ➕ New Type
              </button>
            </>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button className="error-dismiss" onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Entry Form */}
      {showForm && (
        <div className="form-section">
          <div className="section-header">
            <h3>{editingId ? '✏️ Edit Supplier Type' : '➕ New Supplier Type'}</h3>
          </div>

          <div className="form-group-section">
            <div className="form-row">
              <div className="form-group">
                <label>Type Code <span className="required">*</span></label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    name="typecode"
                    value={formData.typecode}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="TYPE001"
                    style={{ flex: 1, textTransform: 'uppercase' }}
                  />
                  {!editingId && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={generateTypeCode}
                      disabled={loading}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      🔄 Generate
                    </button>
                  )}
                </div>
              </div>

              <div className="form-group" style={{ flex: 2 }}>
                <label>Type Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="typename"
                  value={formData.typename}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="e.g., Vendor, Contractor, Service Provider"
                />
              </div>

              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  name="displayorder"
                  value={formData.displayorder}
                  onChange={handleNumberChange}
                  disabled={loading}
                  min="0"
                  placeholder="0"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group" style={{ flex: 2 }}>
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  disabled={loading}
                  rows="3"
                  placeholder="Brief description of this supplier type"
                />
              </div>

              <div className="form-group">
                <label>Icon</label>
                <select
                  name="icon"
                  value={formData.icon}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">-- Select Icon --</option>
                  {iconOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {formData.icon && (
                  <div style={{ marginTop: '5px', fontSize: '2rem', textAlign: 'center' }}>
                    {formData.icon}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Color</label>
                <input
                  type="color"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  disabled={loading}
                  style={{ height: '40px', width: '100%' }}
                />
                {formData.color && (
                  <div style={{ marginTop: '5px', fontSize: '0.85rem', color: '#666' }}>
                    {formData.color}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button 
              className="btn btn-primary" 
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? '⏳ Saving...' : editingId ? '💾 Update' : '💾 Save'}
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={handleCancel}
              disabled={loading}
            >
              ❌ Cancel
            </button>
          </div>
        </div>
      )}

      {/* Search and Grid Section */}
      {!showForm && (
        <div className="grid-section">
          <div className="section-header">
            <h3>📋 Supplier Types List ({filteredTypes.length})</h3>
            <div className="search-box">
              <input
                type="text"
                placeholder="🔍 Search supplier types..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Icon</th>
                  <th>Code</th>
                  <th>Type Name</th>
                  <th>Description</th>
                  <th>Display Order</th>
                  {showWithCount && <th>Suppliers</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && filteredTypes.length === 0 ? (
                  <tr>
                    <td colSpan={showWithCount ? "7" : "6"} className="text-center">Loading...</td>
                  </tr>
                ) : filteredTypes.length === 0 ? (
                  <tr>
                    <td colSpan={showWithCount ? "7" : "6"} className="text-center">
                      {searchTerm ? 'No supplier types found matching your search.' : 'No supplier types found. Click "New Type" to create one.'}
                    </td>
                  </tr>
                ) : (
                  filteredTypes.map(type => (
                    <tr key={type.suppliertypeid}>
                      <td style={{ fontSize: '1.5rem', textAlign: 'center' }}>
                        {type.icon || '📋'}
                      </td>
                      <td>
                        <strong>{type.typecode}</strong>
                      </td>
                      <td>
                        <strong style={{ color: type.color || 'inherit' }}>
                          {type.typename}
                        </strong>
                      </td>
                      <td>{type.description || '-'}</td>
                      <td className="text-center">{type.displayorder}</td>
                      {showWithCount && (
                        <td className="text-center">
                          <span className="badge">{type.suppliercount || 0}</span>
                        </td>
                      )}
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-icon btn-edit"
                            onClick={() => handleEdit(type)}
                            title="Edit"
                            disabled={loading}
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-icon btn-delete"
                            onClick={() => handleDelete(type.suppliertypeid)}
                            title="Delete"
                            disabled={loading}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierTypes;